'use client'

import { faSnowflake } from '@fortawesome/pro-solid-svg-icons'
import { FormattedMessage } from 'react-intl'
import { styled } from 'styled-components'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { Button, ButtonSizes } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import { FeatureFlags } from '@/helpers/featureFlags'

import { FiltersButtonIcon } from '../shared/listings-ui'
import { useFreezeSortToggle } from './useFreezeSortToggle'

const BannerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.5rem;
  padding: 0.5rem 1rem;
  border: 0.0625rem solid ${Colors.Semantic.Info.highlight};
  border-left-width: 0.25rem;
  border-radius: 0.25rem;
  background-color: ${Colors.Semantic.Info.background};
  ${Typography.Body3}

  @media ${Breakpoints.max.small} {
    flex-direction: column;
    align-items: flex-start;
  }
`

const Message = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.375rem;
  color: ${Colors.Semantic.Neutral.black};

  strong {
    font-weight: 600;
    white-space: nowrap;
  }
`

const UnfreezeButton = styled(Button)`
  flex-shrink: 0;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;

  @media ${Breakpoints.max.small} {
    align-self: stretch;
  }
`

/**
 * "Sort frozen" banner (GLOB-4832, Req 3): reinforces the toggle's active
 * styling with an unambiguous, always-visible confirmation that the list
 * order is locked, plus a second way to unfreeze. Renders on both desktop and
 * mobile (unlike the toolbar toggle itself, which mobile reaches via the
 * filters modal instead — see FiltersModal) since the frozen state can
 * persist after that modal closes.
 */
export const FreezeSortBanner = ({
  pageType,
}: {
  pageType: 'alp' | 'storefront'
}) => {
  const enableFreezeSort = useFlag(FeatureFlags.ENABLE_FREEZE_SORT)
  const { isFrozen, setFrozen } = useFreezeSortToggle(pageType)

  if (!enableFreezeSort || !isFrozen) {
    return null
  }

  return (
    <BannerContainer>
      <Message>
        <FiltersButtonIcon icon={faSnowflake} />
        <FormattedMessage
          id="Listings.freezeSort.banner.message"
          values={{
            strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
          }}
        />
      </Message>
      <UnfreezeButton
        appearance="secondary"
        size={ButtonSizes.small}
        onClick={() => setFrozen(false)}
      >
        <FormattedMessage id="Listings.freezeSort.banner.unfreeze" />
      </UnfreezeButton>
    </BannerContainer>
  )
}
